import { useGetOneEstimationMethod } from "#hooks/adapters/useEstimationMethods";
import { useGetOneNetwork } from "#hooks/adapters/useNetworks";
import { useParams } from "#routers/hooks";
import { NetworkDetailPageParameters } from "#routes/organization/networks/[networkId]/detail";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { displayValueByDataType } from "#src/utils/display";
import { getMeasurementTypeFromNetworkCalculatorEstimationMethod } from "#src/view-models/asset-groups";
import {
  KeyValuePanel,
  StorageKeys,
  type KeyValuePanelProps,
} from "@validereinc/common-components";
import { AssetType } from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toTitleCaseString,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { ReactNode } from "react";
import styles from "./NetworkDetailsTab.module.scss";

const cx = classNames.bind(styles);

export const NetworkDetailsPanel = ({
  viewConfigStorageKey,
  templatedConfigurationLink,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  templatedConfigurationLink?: ReactNode;
}) => {
  const { localize } = useLocalization();
  const { networkId } = useParams<NetworkDetailPageParameters>();
  const [filters] = useStickyState<{ period?: { from?: string } }>(
    {},
    viewConfigStorageKey
  );

  const { data: assetGroup } = useGetOneNetwork(networkId, {
    period: getYearMonthFromDateRange(filters.period),
  });
  const { data: defaultEstimationMethod } = useGetOneEstimationMethod({
    id: assetGroup?.default_estimation_method_id ?? "",
    entityType: AssetType.ASSET_GROUP,
  });
  const measurementType = defaultEstimationMethod
    ? toTitleCaseString(
        getMeasurementTypeFromNetworkCalculatorEstimationMethod(
          defaultEstimationMethod.data
        )
      )
    : "";
  const customAttributesQuery = useListCustomAttributes({
    filters: { entity_type: AssetType.ASSET_GROUP },
  });

  const networkCustomAttributes = customAttributesQuery.data?.data ?? [];

  const data: KeyValuePanelProps["data"] = [
    ...(templatedConfigurationLink
      ? [
          {
            title: "Asset Builder",
            value: templatedConfigurationLink,
          },
        ]
      : []),
    ...(measurementType
      ? [
          {
            title: "Measurement Type",
            value: measurementType,
          },
        ]
      : []),
    {
      title: localize("facility_plural"),
      // @ts-expect-error TODO: Typing on assetGroup
      value: assetGroup?.assets?.filter(
        ({ asset_type }) => asset_type === AssetType.FACILITY
      )?.length,
    },
    {
      title: localize("equipment_plural"),
      // @ts-expect-error TODO: Typing on assetGroup
      value: assetGroup?.assets?.filter(
        ({ asset_type }) => asset_type === AssetType.EQUIPMENT
      )?.length,
    },
    {
      title: "Devices",
      // @ts-expect-error TODO: Typing on assetGroup
      value: assetGroup?.assets?.filter(
        ({ asset_type }) => asset_type === AssetType.DEVICE
      )?.length,
    },
    {
      title: localize("flow_plural"),
      // @ts-expect-error TODO: Typing on assetGroup
      value: assetGroup?.assets?.filter(
        ({ asset_type }) => asset_type === AssetType.FLOW
      )?.length,
    },
    ...networkCustomAttributes.map(
      ({ display_name, field_name, data_type }) => {
        const value = assetGroup?.custom_attributes?.[field_name];

        return {
          title: display_name,
          value: displayValueByDataType(value, data_type, {
            alignment: "right",
          }),
        };
      }
    ),
  ];

  return (
    <div className={cx("flexedContainer")}>
      <KeyValuePanel
        panelMaxColumnCount={2}
        panelKeyValueListProps={{ maxRowCount: 6, variant: "shaded" }}
        data={data}
      />
    </div>
  );
};
